import L, { LatLng, PolylineOptions } from 'leaflet';
import { FeatureGroup, Marker, useMap } from 'react-leaflet';
import {
	iconEnd,
	iconPOI_Highlighted,
	iconStart,
} from '../MapComponents/mapIcons';
import React, { useEffect, useRef } from 'react';
import { Trail } from '../../models/Trail';
import { getTrailPathLatLngs, toLatLng } from './mapPointUtils';

const LINE_OPTIONS: PolylineOptions = {
	color: '#666',
	opacity: 1.0,
	fill: false,
};

const ACTIVE_LINE_OPTIONS: PolylineOptions = {
	color: '#f00',
	opacity: 1.0,
	fill: false,
};

// @ts-ignore
function PolylineDecorator({ polyline, isActive }) {
	const polyRef = useRef<L.Polyline[]>([]);
	const map = useMap();
	useEffect(() => {
		if (!map) {
			return;
		}

		polyRef.current.forEach((line) => {
			map.removeLayer(line);
		});
		polyRef.current = [];

		const lineStyle = isActive ? ACTIVE_LINE_OPTIONS : LINE_OPTIONS;
		const line = L.polyline(polyline, lineStyle);
		polyRef.current.push(line);
		line.addTo(map);

		return () => {
			polyRef.current.forEach((activeLine) => {
				map.removeLayer(activeLine);
			});
			polyRef.current = [];
		};
	}, [isActive, map, polyline]);

	return null;
}

type ShowTrailProps = {
	trail: Trail;
	isActive: boolean;
	hideIntermediateMarkers?: boolean;
};

const ShowTrail: React.FC<ShowTrailProps> = ({
	trail,
	isActive,
	hideIntermediateMarkers = false,
}) => {
	const svgPoints: LatLng[] = getTrailPathLatLngs(trail);
	const poiMarkerPoints = trail.waypoints
		.map((point) => (point.is_poi ? toLatLng(point) : null))
		.filter((value): value is LatLng => value !== null);
	const markerPoints: LatLng[] = (() => {
		if (svgPoints.length === 0) {
			return [];
		}
		const withEnds = [
			svgPoints[0],
			...poiMarkerPoints,
			...(svgPoints.length > 1 ? [svgPoints[svgPoints.length - 1]] : []),
		];
		return withEnds.filter((point, index) => {
			if (index === 0) {
				return true;
			}
			const prev = withEnds[index - 1];
			return prev.lat !== point.lat || prev.lng !== point.lng;
		});
	})();

	return (
		<>
			<FeatureGroup key={`trail-line-${trail.id}`}>
				<PolylineDecorator polyline={svgPoints} isActive={isActive} />
			</FeatureGroup>
			{isActive && markerPoints.length > 0 && (
				<FeatureGroup key={`trail-${trail.id}-points`}>
					{markerPoints.map((p, idcc) => {
						const isStart = idcc === 0;
						const isEnd = idcc === markerPoints.length - 1;
						if (hideIntermediateMarkers && !isStart && !isEnd) {
							return null;
						}
						return (
							<Marker
								key={`trail-${trail.id}-point.${idcc}`}
								position={p}
								icon={
									isStart
										? iconStart
										: isEnd
										? iconEnd
										: iconPOI_Highlighted
								}></Marker>
						);
					})}
				</FeatureGroup>
			)}
		</>
	);
};

export default ShowTrail;
